import { z } from "zod/v4-mini";

import { ApiDataSourceSpec, createApiDatasource, publicApiDataSource } from "@/io/datasource/ApiDatasource.ts";
import { LocalStorageDataSourceSpec, localStorageDataSource } from "@/io/datasource/LocalStorageDatasource.ts";
import { RefreshedTokens, TokenPair, refreshedTokensSchema, tokenPairSchema } from "@/models/AuthModel.ts";
import { cz } from "@/utils/zodUtils.ts";

export class AuthRepository {
  constructor(
    private readonly localStorage: LocalStorageDataSourceSpec = localStorageDataSource,
    private readonly publicDatasource: ApiDataSourceSpec = publicApiDataSource,
  ) {}

  /*******************************/
  /************* API *************/
  /*******************************/

  refreshTokens = (refresh: string): Promise<RefreshedTokens> =>
    this.publicDatasource.post("/auth/token/refresh/", refreshedTokensSchema, {
      refresh,
    });

  revokeRefreshToken = (refresh: string): Promise<undefined> =>
    this.publicDatasource.post("/auth/logout/", cz.ignore(), {
      refresh,
    });

  /**
   * Exchanges a legacy Keycloak token for a Steto token pair. Uses a throwaway datasource so
   * the shared `apiDataSource` (still authenticated by Keycloak) is left untouched.
   */
  legacyTokenToStetoToken = async (legacyToken: string): Promise<TokenPair> => {
    const datasource = createApiDatasource();
    datasource.createInstance(legacyToken);
    const result = await datasource.post("/auth/session/bootstrap/", tokenPairSchema);
    datasource.destroyInstance();
    return result;
  };

  /*******************************/
  /*********** STORAGE ***********/
  /*******************************/

  getRefreshToken = () => this.localStorage.retrieve("unauthenticated", "refreshToken", z.string());

  setRefreshToken = (token: string) => this.localStorage.store("unauthenticated", "refreshToken", token);

  deleteTokens = (): Promise<undefined> => this.localStorage.remove("unauthenticated", "refreshToken");
}
